
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Discrepancy } from "@/types/discrepancy";

type DiscrepancyStatus = "pending" | "salary_deduction" | "forgiven" | "justified" | "validated_as_revenue";

export const useDiscrepancyManagement = () => {
  const queryClient = useQueryClient();

  const { data: discrepancies = [] } = useQuery({
    queryKey: ["discrepancies"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("Non authentifié");
      }

      const { data, error } = await supabase
        .from("counting_discrepancies")
        .select("*")
        .eq("created_by", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return data as Discrepancy[];
    },
  });

  const handleStatusUpdate = async (
    id: string,
    status: DiscrepancyStatus,
    explanation?: string,
    options?: {
      isSalaryDeduction?: boolean;
      salaryDeductionEmployee?: string;
      isForgiven?: boolean;
    }
  ) => {
    try {
      const { error } = await supabase
        .from("counting_discrepancies")
        .update({
          status,
          explanation: explanation || null,
          reviewed_at: new Date().toISOString(),
          is_salary_deduction: options?.isSalaryDeduction || false,
          salary_deduction_employee: options?.salaryDeductionEmployee || null,
          is_forgiven: options?.isForgiven || false,
        })
        .eq("id", id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["discrepancies"] });
      toast.success("Statut mis à jour avec succès");
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut:", error);
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  return {
    discrepancies,
    handleStatusUpdate,
  };
};
